import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import type { QueueInsight } from '../utils/appointmentManager';

interface LiveQueueCardProps {
  insight: QueueInsight | null | undefined;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  lastUpdatedAt?: Date | null;
}

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return 'TBD';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'TBD';
  return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatLastUpdated = (date: Date | null | undefined): string => {
  if (!date) return 'Never';
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const LiveQueueCard: React.FC<LiveQueueCardProps> = ({ insight, onRefresh, isRefreshing = false, lastUpdatedAt }) => {
  if (!insight) return null;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Live Queue</Text>
      <Text style={styles.line}>Your number: #{insight.yourNumber}  |  Ahead: {insight.peopleAhead}</Text>
      <Text style={styles.line}>Current serving: {insight.currentServingNumber ? `#${insight.currentServingNumber}` : 'N/A'}</Text>
      <Text style={styles.line}>
        ETA: {formatTime(insight.estimatedStartAt)} ({formatTime(insight.estimatedWindowStartAt)}-{formatTime(insight.estimatedWindowEndAt)})
      </Text>
      <Text style={styles.meta}>Confidence: {insight.confidence}</Text>
      <View style={styles.footerRow}>
        <Text style={styles.lastUpdatedText}>Updated: {formatLastUpdated(lastUpdatedAt)}</Text>
        {!!onRefresh && (
          <TouchableOpacity
            style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
            onPress={onRefresh}
            disabled={isRefreshing}
          >
            <Text style={styles.refreshButtonText}>{isRefreshing ? 'Refreshing...' : 'Refresh'}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    borderRadius: 10,
    backgroundColor: '#EEF6FF',
    borderWidth: 1,
    borderColor: '#CFE5FF',
    padding: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0B4A8B',
    marginBottom: 4,
  },
  line: {
    fontSize: 13,
    color: '#0B4A8B',
    marginBottom: 2,
  },
  meta: {
    marginTop: 2,
    fontSize: 12,
    color: '#2B6CB0',
  },
  footerRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  lastUpdatedText: {
    fontSize: 11,
    color: '#4A6FA5',
  },
  refreshButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#0B7CFF',
  },
  refreshButtonDisabled: {
    opacity: 0.7,
  },
  refreshButtonText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
});

export default LiveQueueCard;
